import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";

type Tone = "info" | "success" | "error";
interface Toast {
  id: number;
  tone: Tone;
  msg: string;
}

interface ToastApi {
  show: (msg: string, tone?: Tone) => void;
  success: (msg: string) => void;
  error: (msg: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const c = useContext(Ctx);
  if (!c) throw new Error("useToast outside ToastProvider");
  return c;
}

const ICON: Record<Tone, string> = {
  info: "info",
  success: "check-circle",
  error: "alert-circle",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const remove = useCallback(
    (id: number) => setToasts((t) => t.filter((x) => x.id !== id)),
    [],
  );

  const show = useCallback(
    (msg: string, tone: Tone = "info") => {
      const id = ++seq.current;
      setToasts((t) => [...t, { id, tone, msg }]);
      setTimeout(() => remove(id), tone === "error" ? 7000 : 4500);
    },
    [remove],
  );

  const api: ToastApi = {
    show,
    success: (m) => show(m, "success"),
    error: (m) => show(m, "error"),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-wrap">
          {toasts.map((t) => (
            <div key={t.id} className={`toast ${t.tone}`} role="status">
              <span className="ico">
                <Icon name={ICON[t.tone]} size={18} />
              </span>
              <span className="grow">{t.msg}</span>
              <button
                className="close"
                onClick={() => remove(t.id)}
                aria-label="Dismiss"
              >
                <Icon name="x" size={16} />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}
